import React from 'react'
import { Typography } from 'antd'
import { contract, VERSION } from '@/constants/common-constants'

const { Title, Text, Link } = Typography

interface FooterLink {
    label: string
    href: string
    external?: boolean
}

interface FooterSection {
    title: string
    links: FooterLink[]
}

const GlobalFooter: React.FC = () => {
    // 代码生成器核心功能
    const generatorLinks: FooterLink[] = [
        { label: '控制台代码生成器', href: '/console-generator' },
        { label: 'GUI代码生成器', href: '/gui-generator' },
        { label: 'Web应用代码生成器', href: '/web-generator' },
    ]

    // 其他服务（精选）
    const serviceLinks: FooterLink[] = [
        { label: 'ER图生成服务', href: 'https://tools.anqstar.com/tools', external: true },
        { label: 'SQL转三线表', href: 'https://tools.anqstar.com/tools', external: true },
        { label: 'AI流程图', href: 'https://tools.anqstar.com/tools', external: true },
        { label: 'AIGC检测', href: 'https://tools.anqstar.com/tools', external: true },
        { label: '论文降重', href: 'https://tools.anqstar.com/tools', external: true },
        { label: '更多服务...', href: 'https://tools.anqstar.com/tools', external: true },
    ]

    // 其他菜单栏
    const footerSections: FooterSection[] = [
        {
            title: '其他产品',
            links: [
                { label: '校园小助手', href: 'https://tools.anqstar.com/tools', external: true },
                { label: '启星慧图', href: 'https://draw.anqstar.com/template', external: true },
                { label: '启星AiPPT', href: 'https://ppt.anqstar.com', external: true },
            ],
        },
        {
            title: '帮助中心',
            links: [
                { label: '隐私政策', href: 'https://www.anqstar.com/privacy', external: true },
                { label: '问题反馈', href: 'https://www.anqstar.com/user/activity?active=bug', external: true },
            ],
        },
    ]

    // 底部链接
    const bottomLinks: FooterLink[] = [
        { label: '联系我们', href: 'mailto:contact@anqstar.com', external: true },
        { label: 'B站', href: contract.bilibili_er, external: true },
        { label: 'QQ频道', href: contract.qq_chanel, external: true },
        { label: '微信公众号', href: contract.wx_gzh, external: true },
    ]

    return (
        <footer className="border-t border-gray-200 mt-16" style={{ backgroundColor: '#faf9f6' }}>
            <div className="max-w-[1400px] mx-auto px-12 py-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6">
                    {/* 品牌区域 */}
                    <div className="lg:col-span-3">
                        <div className="flex items-center gap-3 mb-4">
                            <img src="/logo.svg" alt="启星代码生成器" className="w-8 h-8" />
                            <Title level={4} className="!mb-0 !text-gray-800">
                                启星代码生成器
                            </Title>
                        </div>
                        <Text className="text-gray-600 text-sm leading-relaxed block">
                            专业的代码生成工具平台，提供控制台项目、GUI应用、Web应用等多种类型的代码自动化生成服务，帮助学生快速搭建项目框架，提升开发效率。
                        </Text>
                    </div>

                    {/* 代码生成器 */}
                    <div className="lg:col-span-2">
                        <Title level={5} className="!text-gray-800 !text-base !font-semibold !mb-4">
                            代码生成器
                        </Title>
                        <ul className="space-y-3">
                            {generatorLinks.map((link) => (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        className="text-gray-600 hover:text-blue-500 text-sm transition-colors"
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* 其他服务 */}
                    <div className="lg:col-span-2">
                        <Title level={5} className="!text-gray-800 !text-base !font-semibold !mb-4">
                            其他服务
                        </Title>
                        <ul className="space-y-3">
                            {serviceLinks.map((link) => (
                                <li key={link.href + link.label}>
                                    <Link
                                        href={link.href}
                                        target={link.external ? '_blank' : undefined}
                                        rel={link.external ? 'noopener noreferrer' : undefined}
                                        className="text-gray-600 hover:text-blue-500 text-sm transition-colors"
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* 其他菜单栏（其他产品、帮助中心、法律信息） */}
                    <div className="lg:col-span-5 grid grid-cols-3 gap-4">
                        {footerSections.map((section) => (
                            <div key={section.title}>
                                <Title level={5}
                                       className="!text-gray-800 !text-base !font-semibold !mb-4 whitespace-nowrap">
                                    {section.title}
                                </Title>
                                <ul className="space-y-3">
                                    {section.links.map((link) => (
                                        <li key={link.href}>
                                            <Link
                                                href={link.href}
                                                target={link.external ? '_blank' : undefined}
                                                rel={link.external ? 'noopener noreferrer' : undefined}
                                                className="text-gray-600 hover:text-blue-500 text-sm transition-colors whitespace-nowrap"
                                            >
                                                {link.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 底部备案信息 */}
            <div className="border-t border-gray-200" style={{ backgroundColor: '#faf9f6' }}>
                <div className="max-w-[1400px] mx-auto px-12 py-6">
                    <div className="flex flex-wrap justify-center items-center gap-2 text-sm">
                        <Link
                            href="https://beian.miit.gov.cn/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-500 hover:text-blue-500 text-sm transition-colors"
                        >
                            © 苏ICP备2025185291号-1
                        </Link>
                        <span className="text-gray-400">|</span>
                        <Text className="text-gray-500 text-sm">v{VERSION}</Text>
                        {bottomLinks.map((link, index) => (
                            <React.Fragment key={link.href}>
                                <span className="text-gray-400">|</span>
                                <Link
                                    href={link.href}
                                    target={link.external ? '_blank' : undefined}
                                    rel={link.external ? 'noopener noreferrer' : undefined}
                                    className="text-gray-500 hover:text-blue-500 text-sm transition-colors"
                                >
                                    {link.label}
                                </Link>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </div>
        </footer>
    )
}

export default GlobalFooter
